import type { ReactNode } from "react";

// ---------------------------------------------------------------------------
// FormField — labeled wrapper for any form control
// ---------------------------------------------------------------------------

interface FormFieldProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
  className?: string;
}

export function FormField({ label, htmlFor, required, error, children, className = "" }: Readonly<FormFieldProps>) {
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-content-secondary mb-1.5">
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FormInput — styled text input
// ---------------------------------------------------------------------------

interface FormInputProps {
  id?: string;
  type?: "text" | "number" | "url" | "email" | "password";
  value: string | number | "";
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
  min?: number;
  className?: string;
}

const INPUT_CLASS =
  "w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary";

export function FormInput({
  id,
  type = "text",
  value,
  onChange,
  placeholder,
  required,
  maxLength,
  min,
  className = "",
}: Readonly<FormInputProps>) {
  return (
    <input
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      maxLength={maxLength}
      min={min}
      className={`${INPUT_CLASS} ${className}`}
    />
  );
}

// ---------------------------------------------------------------------------
// FormTextarea — styled multiline text input
// ---------------------------------------------------------------------------

interface FormTextareaProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  mono?: boolean;
  className?: string;
}

export function FormTextarea({
  id,
  value,
  onChange,
  placeholder,
  rows = 3,
  mono = false,
  className = "",
}: Readonly<FormTextareaProps>) {
  return (
    <textarea
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className={`${INPUT_CLASS} resize-none ${mono ? "font-mono text-sm" : ""} ${className}`}
    />
  );
}

// ---------------------------------------------------------------------------
// FormSelect — styled native select
// ---------------------------------------------------------------------------

interface FormSelectOption {
  value: string;
  label: string;
}

interface FormSelectProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: FormSelectOption[];
  placeholder?: string;
  className?: string;
}

export function FormSelect({ id, value, onChange, options, placeholder, className = "" }: Readonly<FormSelectProps>) {
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={`${INPUT_CLASS} ${className}`}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

// ---------------------------------------------------------------------------
// FormActions — standard Cancel + primary action button pair
// ---------------------------------------------------------------------------

interface FormActionsProps {
  onCancel: () => void;
  /** Called when the primary button is clicked (use for non-form contexts). If inside a <form>, omit this and rely on type="submit". */
  onSubmit?: () => void;
  submitLabel?: string;
  saving?: boolean;
  disabled?: boolean;
  /** Use full-width buttons separated by a top border (modal style) */
  modal?: boolean;
}

export function FormActions({
  onCancel,
  onSubmit,
  submitLabel = "Save Changes",
  saving = false,
  disabled = false,
  modal = false,
}: Readonly<FormActionsProps>) {
  if (modal) {
    return (
      <div className="flex items-center gap-3 pt-4 border-t border-border-primary">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type={onSubmit ? "button" : "submit"}
          onClick={onSubmit}
          disabled={saving || disabled}
          className="flex-1 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {saving ? "Saving..." : submitLabel}
        </button>
      </div>
    );
  }

  return (
    <div className="flex justify-end gap-2">
      <button
        type="button"
        onClick={onCancel}
        className="rounded-lg px-3 py-1.5 text-sm text-content-tertiary hover:text-content-primary"
      >
        Cancel
      </button>
      <button
        type={onSubmit ? "button" : "submit"}
        onClick={onSubmit}
        disabled={saving || disabled}
        className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {saving ? "Saving..." : submitLabel}
      </button>
    </div>
  );
}
